import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {CustomerOverViewService} from '~features/customer-overview/customer-overview.service';
import {ActionButton, AuthenticatedUser, CrmMembership, Customer} from '~core/models';
import {MembershipService, StorageService} from '~core/services';
import {TablesTranslations} from '~shared/components/tables/tables-translations.service';
import {ActionButtonType} from '~core/enum';
import {MembershipStatusTranslationsService, TooltipTranslationService} from '~shared/translations';

/**
 * Handles membership management
 */
@UntilDestroy()
@Component({
  selector: 'app-crm-membership-list',
  templateUrl: './crm-membership-list.component.html',
  styleUrls: ['../../../../customer-overview.component.scss']
})
export class CrmMembershipListComponent implements OnInit, OnDestroy {
  public authenticatedUser: AuthenticatedUser;
  public loading: boolean = true;
  public modalOpened: boolean = false;
  public currentMembership: Array<CrmMembership>;

  public columns: Array<string> = [];
  public actionButtons: Array<ActionButton> = [];
  public keys: Array<Array<string>> = [];

  @Input() public customer: Customer;
  @Input() public isPanelOpened: boolean = true;
  @Output() public isPanelOpenedChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(
    public customerOverviewService: CustomerOverViewService,
    public membershipService: MembershipService,
    public membershipStatusTranslations: MembershipStatusTranslationsService,
    public tablesTranslations: TablesTranslations,
    public tooltipTranslations: TooltipTranslationService,
    private storage: StorageService
  ) {
    this.storage.castAuthenticatedUser.pipe(untilDestroyed(this)).subscribe(user => this.authenticatedUser = user);
    this.membershipService.castCurrentMembership.pipe(untilDestroyed(this))
      .subscribe(data => {
        if (data !== null) {
          this.currentMembership = [data];
        } else {
          this.currentMembership = data;
        }
      });

    this.columns.push(this.tablesTranslations.i18nJoinDate, this.tablesTranslations.i18nMembershipNr,
      this.tablesTranslations.i18nComment, this.tablesTranslations.i18nStatus);
    this.keys.push(['joinDate', 'membershipNumber', 'comment', 'status']);
    this.actionButtons.push(
      new ActionButton({
        type: ActionButtonType.DELETE,
        icon: '#esf-icon-trash',
        tooltip: this.tooltipTranslations.i18nDelete
      })
    );
  }

  public ngOnInit(): void {
    this.membershipService.announceCurrentMembership(this.customer.membership);
  }

  public ngOnDestroy(): void {
    this.membershipService.clearCurrentMembership();
  }

  /**
   * Open the edit membership modal and pass the address data to it.
   */
  public editMembership(membership: CrmMembership): any {
    this.customerOverviewService.toggleAllModal(false);
    this.customerOverviewService.toggleEditCrmMembershipModal(true);
    this.membershipService.announceCurrentMembership(membership);
  }

  /**
   * Open the edit membership modal and pass the address data to it.
   */
  public createMembership(): any {
    this.customerOverviewService.toggleAllModal(false);
    this.customerOverviewService.toggleCreateCrmMembershipModal(true);
  }

  /**
   * Function that executes the delete membership process
   */
  public deleteMembershipModal(actionButton: any): void {
    this.customerOverviewService.toggleDeleteModal(true, 'membership', actionButton.data.id);
  }

  /**
   * Deletes the selected membership
   */
  public deleteMembershipData(membershipId: number): any {
    this.membershipService.delete(membershipId).pipe(untilDestroyed(this))
      .subscribe(() => {
        this.customerOverviewService.toggleLoadingSubmitClicked(false);
        this.customerOverviewService.toggleDeleteModal(false, '', null);
        this.customerOverviewService.toggleAllModal(false);
        this.membershipService.clearCurrentMembership();
      });
  }
}
